// src/pages/Team/PersonMarks.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The marks one person has been given, on their page, and a
//               button to give them another.
//
// WHAT SOMEBODY HAS BEEN NOTICED FOR, AS MOMENTS.
//
// An owner opening a salesperson sees the last few marks with who gave them,
// which tells him more about whether his manager is watching the floor than
// any count could. An owner opening a manager sees what the manager has been
// recognised for, which is usually nothing, which is its own useful fact
// about the owner.
//
// NO TOTAL. The most recent six, newest first, and nothing that adds them up.
// tests/marks.test.mjs holds the rule for the other two screens and this one
// follows it.
//
// A failed read renders nothing. A missing section is a thing somebody looks
// for; "never recognised" about a person who was is a thing they act on.

import { useCallback, useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Button, useDisclosure } from '@chakra-ui/react'
import Medallion from '../../components/ui/Medallion.jsx'
import GiveMark from '../Manager/GiveMark.jsx'
import { markByKey } from '../../content/marks'
import { firstNameFor } from '../../lib/names.js'
import { listFor, listPeople, dayLabel, lastMarkFor } from '../../lib/recognitions'

export default function PersonMarks({ person, viewerId, dealershipId, canGive }) {
  const [marks, setMarks] = useState(null)
  const [givers, setGivers] = useState({})
  const sheet = useDisclosure()

  const load = useCallback(async () => {
    const { data } = await listFor(person.id, 6)
    setMarks(data)
    if (data && data.length) {
      const who = await listPeople(data.map((r) => r.given_by))
      if (who.data) setGivers(Object.fromEntries(who.data.map((u) => [u.id, u])))
    }
  }, [person.id])

  useEffect(() => {
    load()
  }, [load])

  if (marks === null) return null
  const first = firstNameFor(person) || 'them'
  const giveable = canGive && viewerId && viewerId !== person.id
  if (marks.length === 0 && !giveable) return null
  const last = lastMarkFor(person.id, marks)

  return (
    <Box>
      <HStack justify="space-between" align="baseline" mb={3} flexWrap="wrap" gap={2}>
        <Text textStyle="eyebrow" color="inkDim">NOTICED</Text>
        {giveable && (
          <Button size="xs" variant="outline" onClick={sheet.onOpen}>
            Recognise {first}
          </Button>
        )}
      </HStack>

      {marks.length === 0 ? (
        <Text fontSize="sm" color="inkMuted" maxW="measure" lineHeight={1.55}>
          Nobody has given {first} a mark yet. If you saw them run a step well, say so, here and out
          loud.
        </Text>
      ) : (
        <VStack align="stretch" spacing={0}>
          {marks.map((r) => {
            const m = markByKey(r.mark)
            const from = r.given_by === viewerId ? 'you' : firstNameFor(givers[r.given_by]) || 'their manager'
            return (
              <HStack key={r.id} spacing={3} py={2.5} borderTop="1px solid" borderColor="line" align="flex-start">
                <Medallion mark={r.mark} size={32} />
                <Box minW={0}>
                  <Text fontSize="body-sm" lineHeight={1.4}>
                    <Text as="span" fontWeight={600}>{m?.name || 'A mark'}</Text>
                    <Text as="span" color="inkMuted">
                      {' · '}from {from}, {dayLabel(r.created_at)}
                    </Text>
                  </Text>
                  {r.note && (
                    <Text fontSize="sm" color="inkMuted" lineHeight={1.45} mt={0.5}>
                      “{r.note}”
                    </Text>
                  )}
                </Box>
              </HStack>
            )
          })}
        </VStack>
      )}

      {giveable && (
        <GiveMark
          person={person}
          isOpen={sheet.isOpen}
          onClose={sheet.onClose}
          dealershipId={dealershipId}
          givenBy={viewerId}
          lastMark={last ? { mark: last.mark, when: dayLabel(last.created_at) } : null}
          onGiven={() => load()}
        />
      )}
    </Box>
  )
}
